/**
 * Post-Hand Grade screen (06) — per PRD §12 + wireframe
 * `06A_v5_post_hand_grade_*`.
 *
 * Layout: per-decision tabs. Each tab represents one hero decision in
 * history order and is labeled `${Street} · ${Action}` (e.g.
 * `Flop · Check`, `Turn · Bet`, `River · Call`). The mobile tab row is
 * horizontally scrollable with edge-fade affordances; the desktop tab
 * row fits all decisions inline.
 *
 * Grade flow:
 *   1. On mount, check if the session already has a grade for the
 *      active hand (e.g. from a page reload). If yes, display it.
 *   2. Otherwise call gradeService.requestGrade.
 *   3. While the call is in flight, show "Grading…" placeholders.
 *   4. On success, persist via sessionManager.attachGrade and display
 *      the per-decision tabs + score summary.
 *   5. On terminal failure, navigate to /grade/failed.
 *
 * Score computation (PRD §11.2):
 *   - Criteria score: PASS=1, PARTIAL=0.5, MISSING=0, N/A excluded.
 *     Summed across all decisions; displayed as X.X / Y (Z%).
 *   - Time score: aggregate onTime / total across decisions, plus
 *     avgSeconds.
 *   - Default: X / N where N is the count of default-eligible decisions
 *     (river decisions excluded per PRD §11.1).
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { gradeService } from '@/services/grade/gradeService';
import { sessionManager } from '@/services/sessionManager';
import { isTerminal } from '@/lib/engine/stateMachine';
import type {
  CriterionVerdict,
  DecisionGrade,
  HeroActionKind,
  RubricGrade,
} from '@/types/grade';
import { RUBRIC_CRITERIA } from '@/types/grade';
import type { ActionRecord } from '@/types/handState';
import type { Session } from '@/types/session';
import { cn } from '@/lib/utils';

type GradeState =
  | { phase: 'loading' }
  | { phase: 'graded'; grade: RubricGrade }
  | { phase: 'failed'; message?: string };

export function PostHandGradeScreen() {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [gradeState, setGradeState] = useState<GradeState>({
    phase: 'loading',
  });
  const [activeIdx, setActiveIdx] = useState(0);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);

  useEffect(() => {
    const s = sessionManager.loadActiveSession();
    setSession(s);
    setLoaded(true);
  }, []);

  // Kick off grading once we have a loaded session + terminal hand.
  // Skip the request if a grade is already persisted for THIS hand (page
  // reload case). A grade with a mismatching handId is stale carryover
  // from a previous hand and is ignored — we re-grade.
  useEffect(() => {
    if (!session?.activeHand) return;
    if (!isTerminal(session.activeHand)) return;
    const handId = session.activeHand.handId;
    const persisted = session.activeHandGrade;
    if (
      persisted &&
      persisted.handId === handId &&
      Array.isArray(persisted.grade?.decisions)
    ) {
      setGradeState({ phase: 'graded', grade: persisted.grade });
      return;
    }
    let cancelled = false;
    void (async () => {
      const heroActions = session.activeHand!.history.filter(
        (rec) => rec.actor === 'hero',
      );
      const outcome = await gradeService.requestGrade({
        config: session.config,
        state: session.activeHand!,
        heroActions,
      });
      if (cancelled) return;
      if (outcome.status === 'graded') {
        sessionManager.attachGrade(handId, outcome.grade);
        setGradeState({ phase: 'graded', grade: outcome.grade });
      } else if (outcome.status === 'terminal') {
        setGradeState({ phase: 'failed', message: outcome.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  // Terminal failure → CouldntBeGraded screen, with the error message
  // (if any) passed via location state for visible debugging.
  useEffect(() => {
    if (gradeState.phase === 'failed') {
      navigate('/grade/failed', {
        replace: true,
        state: { errorMessage: gradeState.message },
      });
    }
  }, [gradeState, navigate]);

  // Collapse transcript whenever the active tab changes — each decision
  // has its own transcript, and users probably don't want it auto-shown
  // for every tab.
  useEffect(() => {
    setTranscriptExpanded(false);
  }, [activeIdx]);

  if (!loaded) return null;
  if (!session) return <Navigate to="/" replace />;
  if (!session.activeHand) return <Navigate to="/config" replace />;
  if (!isTerminal(session.activeHand)) return <Navigate to="/play" replace />;

  const state = session.activeHand;
  const heroRecords = state.history.filter((rec) => rec.actor === 'hero');
  const handNumber = 1;
  const handsTotal = session.config.handsCount;

  const handleNextHand = () => {
    sessionManager.startNextHand();
    navigate('/play');
  };

  const decisions =
    gradeState.phase === 'graded' ? gradeState.grade.decisions : [];

  // Clamp activeIdx so it's always valid for the rendered decision list.
  const clampedActiveIdx = Math.min(
    Math.max(0, activeIdx),
    Math.max(0, decisions.length - 1),
  );

  const activeDecision = decisions[clampedActiveIdx];
  const activeRecord = activeDecision
    ? heroRecords.find((r) => r.index === activeDecision.actionIndex) ?? null
    : null;

  return (
    <main className="flex min-h-dvh flex-col bg-surface-app text-text-primary">
      <Header
        handNumber={handNumber}
        handsTotal={handsTotal}
        onBack={() => navigate('/resolution')}
      />

      <ScoreSummary gradeState={gradeState} />

      {/* Tab row is hidden during loading — no point showing per-decision
          tabs we don't have data for yet. It pops in when grades arrive. */}
      {gradeState.phase === 'graded' && (
        <DecisionTabRow
          decisions={decisions}
          loading={false}
          loadingPlaceholders={heroRecords.length}
          activeIdx={clampedActiveIdx}
          onSelect={setActiveIdx}
        />
      )}

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-2xl px-4 py-4">
          {gradeState.phase === 'graded' && activeDecision ? (
            <DecisionPanel
              decision={activeDecision}
              record={activeRecord}
              transcriptExpanded={transcriptExpanded}
              onToggleTranscript={() => setTranscriptExpanded((v) => !v)}
            />
          ) : gradeState.phase === 'graded' && decisions.length === 0 ? (
            <div className="rounded-md border border-border bg-surface-elevated p-3 text-xs text-text-muted">
              No hero decisions graded for this hand.
            </div>
          ) : (
            <LoadingPanel />
          )}
        </div>
      </div>

      <div className="flex min-h-[5rem] items-center border-t border-border bg-surface-elevated px-4">
        <div className="mx-auto flex w-full max-w-2xl justify-end">
          <button
            type="button"
            onClick={handleNextHand}
            className={cn(
              'rounded-md bg-brand-primary px-5 py-2.5 text-sm font-semibold',
              'text-text-on-brand hover:bg-brand-primary-hover',
              'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-1',
            )}
          >
            Next Hand →
          </button>
        </div>
      </div>
    </main>
  );
}

// =============================================================================
// Score computation (PRD §11.2)
// =============================================================================

interface CriteriaScore {
  earned: number;
  total: number;
  pct: number;
}

/**
 * Sum criteria points across every decision (PRD §11.2). Each active
 * (non-NA) criterion on each decision counts as 1 point; PASS=1,
 * PARTIAL=0.5, MISSING=0.
 */
function computeCriteriaScore(grade: RubricGrade): CriteriaScore {
  let earned = 0;
  let total = 0;
  for (const d of grade.decisions) {
    for (const name of RUBRIC_CRITERIA) {
      const c = d.criteria[name];
      if (!c || c.verdict === 'NA') continue;
      total += 1;
      if (c.verdict === 'PASS') earned += 1;
      else if (c.verdict === 'PARTIAL') earned += 0.5;
    }
  }
  const pct = total === 0 ? 0 : Math.round((earned / total) * 100);
  return { earned, total, pct };
}

/** Count default-followed / default-eligible decisions for the summary. */
function defaultSummary(grade: RubricGrade): {
  followed: number;
  eligible: number;
} {
  let followed = 0;
  let eligible = 0;
  for (const d of grade.decisions) {
    if (d.prescribedDefault.verdict === 'NA') continue;
    eligible += 1;
    if (d.prescribedDefault.verdict === 'FOLLOWED') followed += 1;
  }
  return { followed, eligible };
}

// =============================================================================
// Sub-components
// =============================================================================

function Header({
  handNumber,
  handsTotal,
  onBack,
}: {
  handNumber: number;
  handsTotal: number;
  onBack: () => void;
}) {
  return (
    <div className="shrink-0 border-b border-border bg-surface-app px-4 py-3">
      <div className="mx-auto flex max-w-2xl items-center justify-between text-xs">
        <button
          type="button"
          onClick={onBack}
          className="text-text-secondary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus"
        >
          ‹ Back
        </button>
        <div className="text-text-secondary">
          Hand {handNumber} of {handsTotal}
        </div>
      </div>
    </div>
  );
}

function ScoreSummary({ gradeState }: { gradeState: GradeState }) {
  let criteriaBig = '—';
  let criteriaSmall = '—%';
  let timeBig = '—';
  let timeSmall = '—s AVG';
  let defaultBig = '—';
  let defaultSmall = '';
  // Footnote is only used for terminal failure routing. The loading
  // state communicates itself via the big spinner in LoadingPanel and
  // the pulsing score cells — no duplicate "Grading…" message here.
  let footnote: string | null = null;

  if (gradeState.phase === 'graded') {
    const score = computeCriteriaScore(gradeState.grade);
    criteriaBig = `${formatScore(score.earned)}/${score.total}`;
    criteriaSmall = `(${score.pct}%)`;
    const ts = gradeState.grade.timeScore;
    if (ts.total === 0) {
      timeBig = '—';
      timeSmall = 'no timer';
    } else {
      timeBig = `${ts.onTime}/${ts.total}`;
      timeSmall = `${ts.avgSeconds}s AVG`;
    }
    const ds = defaultSummary(gradeState.grade);
    if (ds.eligible === 0) {
      defaultBig = 'N/A';
      defaultSmall = 'not graded';
    } else {
      defaultBig = `${ds.followed}/${ds.eligible}`;
      defaultSmall =
        ds.followed === ds.eligible
          ? 'all followed'
          : ds.followed === 0
            ? 'all deviated'
            : 'some deviated';
    }
    footnote = null;
  } else if (gradeState.phase === 'failed') {
    footnote = 'Grading failed — routing…';
  }

  return (
    <div className="border-b border-border bg-surface-elevated px-4 py-3">
      <div className="mx-auto grid max-w-2xl grid-cols-3 gap-2 text-xs">
        <ScoreCell
          label="CRITERIA"
          big={criteriaBig}
          small={criteriaSmall}
          pulsing={gradeState.phase === 'loading'}
        />
        <ScoreCell
          label="TIME"
          big={timeBig}
          small={timeSmall}
          pulsing={gradeState.phase === 'loading'}
        />
        <ScoreCell
          label="DEFAULT"
          big={defaultBig}
          small={defaultSmall}
          pulsing={gradeState.phase === 'loading'}
        />
      </div>
      {footnote && (
        <div className="mx-auto mt-2 max-w-2xl text-[10px] uppercase tracking-wider text-text-muted">
          {footnote}
        </div>
      )}
    </div>
  );
}

function ScoreCell({
  label,
  big,
  small,
  pulsing = false,
}: {
  label: string;
  big: string;
  small: string;
  pulsing?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
        {label}
      </div>
      <div
        className={cn(
          'font-mono text-base font-bold tabular-nums text-text-primary',
          pulsing && 'animate-pulse text-text-muted',
        )}
      >
        {big}
      </div>
      <div
        className={cn(
          'text-[10px] tabular-nums text-text-muted',
          pulsing && 'animate-pulse',
        )}
      >
        {small}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Decision tab row (horizontally scrollable on overflow)
// -----------------------------------------------------------------------------

function DecisionTabRow({
  decisions,
  loading,
  loadingPlaceholders,
  activeIdx,
  onSelect,
}: {
  decisions: DecisionGrade[];
  loading: boolean;
  loadingPlaceholders: number;
  activeIdx: number;
  onSelect: (idx: number) => void;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [edges, setEdges] = useState<{ left: boolean; right: boolean }>({
    left: false,
    right: false,
  });

  // Compute edge-fade visibility from scroll position. Recomputes on
  // scroll, on tab count change, and on layout/resize.
  const updateEdges = () => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setEdges((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right },
    );
  };

  useLayoutEffect(() => {
    updateEdges();
    const el = scrollRef.current;
    if (!el) return;
    const onResize = () => updateEdges();
    el.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', onResize);
    };
  }, [decisions.length, loading]);

  // Auto-scroll active tab into view on activate.
  useEffect(() => {
    const tab = tabRefs.current[activeIdx];
    if (!tab) return;
    tab.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }, [activeIdx]);

  if (loading) {
    return (
      <div className="border-b border-border bg-surface-app">
        <div className="mx-auto flex max-w-2xl gap-1 px-4 py-2 overflow-hidden">
          {Array.from({ length: Math.max(1, loadingPlaceholders) }).map(
            (_, i) => (
              <div
                key={i}
                className="h-7 w-28 shrink-0 animate-pulse rounded-sm bg-surface-elevated"
              />
            ),
          )}
        </div>
      </div>
    );
  }

  if (decisions.length === 0) {
    return (
      <div className="border-b border-border bg-surface-app">
        <div className="mx-auto max-w-2xl px-4 py-3 text-xs text-text-muted">
          No decisions to display.
        </div>
      </div>
    );
  }

  return (
    <div className="relative border-b border-border bg-surface-app">
      {/* Left edge fade — only when scrolled away from left edge */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-8',
          'bg-gradient-to-r from-surface-app to-transparent',
          'transition-opacity duration-150',
          edges.left ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        ref={scrollRef}
        role="tablist"
        aria-orientation="horizontal"
        className={cn(
          'mx-auto flex max-w-2xl gap-1 overflow-x-auto px-4',
          'snap-x snap-mandatory',
          // Hide scrollbar visually while keeping the scroll affordance.
          // Tailwind has no first-class utility — inline style does it.
          '[scrollbar-width:none] [-ms-overflow-style:none]',
          '[&::-webkit-scrollbar]:hidden',
        )}
      >
        {decisions.map((d, i) => (
          <button
            key={d.actionIndex}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={i === activeIdx}
            onClick={() => onSelect(i)}
            className={cn(
              'shrink-0 snap-start whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition-colors',
              i === activeIdx
                ? 'border-brand-accent text-text-primary'
                : 'border-transparent text-text-muted hover:text-text-secondary',
            )}
          >
            {tabLabel(d)}
          </button>
        ))}
      </div>
      {/* Right edge fade — only when more content to the right */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-8',
          'bg-gradient-to-l from-surface-app to-transparent',
          'transition-opacity duration-150',
          edges.right ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Decision panel (context strip + transcript + per-criterion rows)
// -----------------------------------------------------------------------------

function DecisionPanel({
  decision,
  record,
  transcriptExpanded,
  onToggleTranscript,
}: {
  decision: DecisionGrade;
  record: ActionRecord | null;
  transcriptExpanded: boolean;
  onToggleTranscript: () => void;
}) {
  const ts = decision.timeScore;
  const pd = decision.prescribedDefault;
  const transcript = record?.transcript ?? '';

  return (
    <section className="space-y-3">
      {/* Context strip: Default + Time for this decision */}
      <div className="flex items-center justify-between rounded-md border border-border bg-surface-elevated px-3 py-2 text-[11px] uppercase tracking-wider text-text-muted">
        <div>
          Default ·{' '}
          <span
            className={cn(
              'font-bold',
              pd.verdict === 'FOLLOWED'
                ? 'text-grade-pass'
                : pd.verdict === 'DEVIATED'
                  ? 'text-grade-partial'
                  : 'text-text-muted',
            )}
          >
            {pd.verdict === 'NA'
              ? 'N/A'
              : pd.verdict === 'FOLLOWED'
                ? 'OK'
                : 'DEVIATED'}
          </span>
        </div>
        <div>
          Time ·{' '}
          <span
            className={cn(
              'tabular-nums font-bold',
              ts
                ? ts.onTime
                  ? 'text-grade-pass'
                  : 'text-grade-partial'
                : 'text-text-muted',
            )}
          >
            {ts ? `${ts.seconds}s` : '—'}
          </span>
        </div>
      </div>

      {/* Collapsible transcript header */}
      <button
        type="button"
        onClick={onToggleTranscript}
        aria-expanded={transcriptExpanded}
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-border bg-surface-elevated px-3 py-2 text-[11px] uppercase tracking-wider text-text-secondary',
          'hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus',
        )}
      >
        <span>
          <span className="mr-1">{transcriptExpanded ? '▾' : '▸'}</span>
          Transcript
        </span>
        {!record?.transcript && (
          <span className="text-[10px] normal-case tracking-normal text-text-muted">
            none captured
          </span>
        )}
      </button>
      {transcriptExpanded && (
        <blockquote
          className={cn(
            'rounded-md border border-border bg-surface-elevated p-3 text-sm',
            transcript ? 'text-text-secondary' : 'italic text-text-muted',
          )}
        >
          {transcript || '(no transcript captured for this decision)'}
        </blockquote>
      )}

      {/* Rubric label */}
      <div className="pt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted">
        Rubric
      </div>

      {/* 10 criterion rows */}
      <ul className="divide-y divide-border">
        {RUBRIC_CRITERIA.map((name) => {
          const c = decision.criteria[name];
          return (
            <li
              key={name}
              className="flex items-start justify-between gap-3 py-2.5"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-text-primary">
                  {name}
                </div>
                <div className="mt-0.5 text-xs text-text-muted">
                  {c?.feedback ?? '—'}
                </div>
              </div>
              <VerdictBadge verdict={c?.verdict ?? null} loading={false} />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function LoadingPanel() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-border bg-surface-elevated px-4 py-12">
      <span
        role="status"
        aria-label="Grading in progress"
        className={cn(
          'inline-block h-10 w-10 animate-spin rounded-full border-[3px]',
          'border-border border-t-brand-accent',
        )}
      />
      <div className="text-sm font-semibold uppercase tracking-wider text-text-secondary">
        Auditing your decisions…
      </div>
      <div className="text-[11px] text-text-muted">
        GPT-4o-mini · usually 4–8s
      </div>
    </div>
  );
}

function VerdictBadge({
  verdict,
  loading,
}: {
  verdict: CriterionVerdict | null;
  loading: boolean;
}) {
  if (loading || !verdict) {
    return (
      <div
        className={cn(
          'shrink-0 rounded-sm border border-border bg-surface-app px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-text-muted',
          'animate-pulse',
        )}
      >
        Grading
      </div>
    );
  }
  const color =
    verdict === 'PASS'
      ? 'bg-grade-pass text-text-on-brand border-grade-pass'
      : verdict === 'PARTIAL'
        ? 'bg-grade-partial text-text-on-light border-grade-partial'
        : verdict === 'MISSING'
          ? 'bg-grade-missing text-text-on-brand border-grade-missing'
          : 'border-border bg-surface-app text-text-muted';
  return (
    <div
      className={cn(
        'shrink-0 rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
        color,
      )}
    >
      {verdict === 'NA' ? 'N/A' : verdict}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

const ACTION_LABELS: Record<HeroActionKind, string> = {
  check: 'Check',
  bet: 'Bet',
  call: 'Call',
  raise: 'Raise',
  fold: 'Fold',
  allin: 'All-In',
};

function streetLabel(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function tabLabel(d: DecisionGrade): string {
  return `${streetLabel(d.street)} · ${ACTION_LABELS[d.actionKind] ?? d.actionKind}`;
}

function formatScore(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toFixed(1);
}
